import { RoundedBox } from '@react-three/drei'
import { useMemo } from 'react'
import * as THREE from 'three'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

interface PropsEquipamento {
  cor?: string
  escala?: number
  rotacaoY?: number
  comCunhaAjuste?: boolean
  invertido?: boolean
  fantasma?: boolean
  emDestaque?: boolean
  onClick?: (evento?: any) => void
  onPointerOver?: (evento?: any) => void
  onPointerOut?: (evento?: any) => void
}

const COR_PEAD_PADRAO = '#1c2024'
const COR_CORDA_LARANJA = '#ff5f1f'

/**
 * Calço Escalonado (Step Chock) profissional de desencarceramento.
 *
 * Possui 5 degraus ascendentes para apoio em diferentes alturas sob a caixa de ar,
 * ranhuras antiderrapantes nos degraus e alça de corda técnica de alta visibilidade
 * para inserção/remoção segura sem expor os membros da equipe sob o veículo.
 * Permite também utilização invertida (face plana para cima) quando a superfície
 * da carroceria/teto exige maior área de contato contínua.
 */
export function StepChock3D({
  cor = COR_PEAD_PADRAO,
  escala = 1,
  rotacaoY = 0,
  comCunhaAjuste = false,
  invertido = false,
  fantasma = false,
  emDestaque = false,
  onClick,
  onPointerOver,
  onPointerOut,
}: PropsEquipamento) {
  // Geometria em degraus via perfil extrudado
  const { shapeChock } = useMemo(() => {
    const shape = new THREE.Shape()
    const comprimentoBase = 0.65
    const alturaTotal = 0.28
    const numDegraus = 5
    const dxDegrau = (comprimentoBase * 0.78) / numDegraus
    const dyDegrau = (alturaTotal - 0.05) / numDegraus

    // Origem na quina inferior traseira
    shape.moveTo(-comprimentoBase / 2, 0)
    // Sobe a parede traseira vertical (onde fica a alça)
    shape.lineTo(-comprimentoBase / 2, alturaTotal)

    // Degraus descendentes
    let xAtual = -comprimentoBase / 2
    let yAtual = alturaTotal
    for (let i = 0; i < numDegraus; i++) {
      xAtual += dxDegrau
      shape.lineTo(xAtual, yAtual)
      yAtual -= dyDegrau
      shape.lineTo(xAtual, yAtual)
    }

    // Ponta frontal e base
    shape.lineTo(comprimentoBase / 2, 0)
    shape.lineTo(-comprimentoBase / 2, 0)

    return { shapeChock: shape }
  }, [])

  const larguraExtrusao = 0.16

  return (
    <group
      scale={escala}
      rotation={[0, rotacaoY, 0]}
      onClick={onClick}
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
    >
      <group position={[0, invertido ? 0.28 : 0, 0]} rotation={[invertido ? Math.PI : 0, 0, 0]}>
        {/* Corpo escalonado */}
        <mesh position={[0, 0, -larguraExtrusao / 2]}>
          <extrudeGeometry
            args={[
              shapeChock,
              { depth: larguraExtrusao, bevelEnabled: true, bevelThickness: 0.01, bevelSize: 0.01, bevelSegments: 2 },
            ]}
          />
          {fantasma ? (
            <meshStandardMaterial
              color={emDestaque ? '#38bdf8' : '#0284c7'}
              emissive={emDestaque ? '#38bdf8' : '#0284c7'}
              emissiveIntensity={emDestaque ? 0.85 : 0.4}
              transparent
              opacity={emDestaque ? 0.65 : 0.4}
              depthWrite={false}
              roughness={0.3}
              metalness={0.1}
            />
          ) : (
            <meshStandardMaterial color={cor} roughness={0.82} metalness={0.15} />
          )}
        </mesh>

        {/* Alça de corda técnica de resgate na extremidade alta */}
        <group position={[-0.325, 0.22, 0]}>
          {/* Olhal de passagem da corda */}
          <mesh rotation={[0, Math.PI / 2, 0]}>
            <cylinderGeometry args={[0.015, 0.015, 0.02, 12]} />
            <meshStandardMaterial
              color={fantasma ? (emDestaque ? '#38bdf8' : '#0284c7') : '#0f1115'}
              metalness={0.8}
              roughness={0.3}
              transparent={fantasma}
              opacity={fantasma ? 0.5 : 1}
            />
          </mesh>
          {/* Laço da corda */}
          <mesh position={[-0.07, -0.02, 0]} rotation={[0, 0, Math.PI / 6]}>
            <torusGeometry args={[0.045, 0.009, 8, 24, Math.PI * 1.5]} />
            <meshStandardMaterial
              color={fantasma ? (emDestaque ? '#67e8f9' : '#38bdf8') : COR_CORDA_LARANJA}
              roughness={0.6}
              transparent={fantasma}
              opacity={fantasma ? 0.7 : 1}
            />
          </mesh>
        </group>

        {/* Cunha invertida de ajuste fino (casamento de cunhas +10% de firmeza) */}
        {comCunhaAjuste && !fantasma && (
          <group position={[0.02, 0.16, 0]} rotation={[0, 0, Math.PI]}>
            <CunhaResgate3D escala={0.7} cor="#ea580c" />
          </group>
        )}
      </group>
    </group>
  )
}

/**
 * Cunha de Resgate (Wedge).
 *
 * Utilizada para bloqueio horizontal de pneus contra rolamento
 * ou para ajuste fino de contato sob os calços e soleira.
 */
export function CunhaResgate3D({
  cor = COR_PEAD_PADRAO,
  escala = 1,
  rotacaoY = 0,
  fantasma = false,
  emDestaque = false,
  onClick,
  onPointerOver,
  onPointerOut,
}: PropsEquipamento) {
  const { shapeCunha } = useMemo(() => {
    const shape = new THREE.Shape()
    const comp = 0.3
    const alt = 0.12
    shape.moveTo(-comp / 2, 0)
    shape.lineTo(comp / 2, 0)
    shape.lineTo(-comp / 2, alt)
    shape.lineTo(-comp / 2, 0)
    return { shapeCunha: shape }
  }, [])

  const largura = 0.14

  return (
    <group
      scale={escala}
      rotation={[0, rotacaoY, 0]}
      onClick={onClick}
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
    >
      <mesh position={[0, 0, -largura / 2]}>
        <extrudeGeometry
          args={[
            shapeCunha,
            { depth: largura, bevelEnabled: true, bevelThickness: 0.008, bevelSize: 0.008, bevelSegments: 2 },
          ]}
        />
        {fantasma ? (
          <meshStandardMaterial
            color={emDestaque ? '#34d399' : '#059669'}
            emissive={emDestaque ? '#34d399' : '#059669'}
            emissiveIntensity={emDestaque ? 0.85 : 0.4}
            transparent
            opacity={emDestaque ? 0.65 : 0.4}
            depthWrite={false}
            roughness={0.3}
            metalness={0.1}
          />
        ) : (
          <meshStandardMaterial color={cor} roughness={0.85} metalness={0.12} />
        )}
      </mesh>
      {/* Ranhuras antiderrapantes na rampa */}
      {!fantasma && [-0.08, -0.01, 0.06].map((x, i) => (
        <mesh
          key={i}
          position={[x, 0.03 + (x < 0 ? 0.03 : 0), 0]}
          rotation={[0, 0, -Math.PI / 7]}
        >
          <CaixaArredondada args={[0.012, 0.006, largura * 0.9]} />
          <meshStandardMaterial color="#0b0d10" roughness={0.9} />
        </mesh>
      ))}
    </group>
  )
}

/**
 * Bloco de Cribbing Modular (Estilo Lego / Encaixe de Resgate).
 *
 * Bloco retangular/quadrado sólido com relevos piramidais antiderrapantes no topo
 * e cavidades de empilhamento para montagem estável tipo caixa (box cribbing).
 */
export function BlocoCribbing3D({
  cor = COR_PEAD_PADRAO,
  escala = 1,
  rotacaoY = 0,
  camadas = 1,
  comCunhaTopo = false,
  comCalcoTopo = false,
  fantasma = false,
  emDestaque = false,
  onClick,
  onPointerOver,
  onPointerOut,
}: PropsEquipamento & { camadas?: number; comCunhaTopo?: boolean; comCalcoTopo?: boolean }) {
  const comp = 0.36
  const larg = 0.36
  const alt = 0.12
  const numCamadas = Math.max(1, Math.min(camadas, 5))

  return (
    <group
      scale={escala}
      rotation={[0, rotacaoY, 0]}
      onClick={onClick}
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
    >
      {Array.from({ length: numCamadas }).map((_, idx) => (
        <group key={idx} position={[0, idx * alt, 0]}>
          {/* Corpo principal chanfrado */}
          <mesh position={[0, alt / 2, 0]}>
            <RoundedBox args={[comp, alt, larg]} radius={0.015} smoothness={2}>
              {fantasma ? (
                <meshStandardMaterial
                  color={emDestaque ? '#fbbf24' : '#d97706'}
                  emissive={emDestaque ? '#fbbf24' : '#d97706'}
                  emissiveIntensity={emDestaque ? 0.85 : 0.4}
                  transparent
                  opacity={emDestaque ? 0.65 : 0.4}
                  depthWrite={false}
                  roughness={0.3}
                  metalness={0.1}
                />
              ) : (
                <meshStandardMaterial color={cor} roughness={0.88} metalness={0.1} />
              )}
            </RoundedBox>
          </mesh>

          {/* Matriz 3x3 de pinos piramidais antiderrapantes no topo (tipo Lego) */}
          {[-0.1, 0, 0.1].map((x) =>
            [-0.1, 0, 0.1].map((z) => (
              <mesh key={`${x}-${z}`} position={[x, alt + 0.01, z]}>
                <CaixaArredondada args={[0.05, 0.018, 0.05]} />
                <meshStandardMaterial
                  color={fantasma ? (emDestaque ? '#fbbf24' : '#d97706') : '#111418'}
                  roughness={0.85}
                  transparent={fantasma}
                  opacity={fantasma ? 0.6 : 1}
                />
              </mesh>
            )),
          )}

          {/* Recessos laterais para pega manual */}
          {!fantasma && [-1, 1].map((lado) => (
            <mesh key={lado} position={[lado * (comp / 2), alt / 2, 0]}>
              <CaixaArredondada args={[0.02, 0.04, 0.14]} />
              <meshStandardMaterial color="#0b0d10" roughness={0.9} />
            </mesh>
          ))}
        </group>
      ))}

      {!fantasma && (comCunhaTopo || comCalcoTopo) && (
        <group position={[0, numCamadas * alt, 0]}>
          <CunhaResgate3D cor="#ea580c" escala={0.9} />
        </group>
      )}
    </group>
  )
}

